import type { ContentCard, Episode } from '@/lib/catalog'
import { byId, clear, el, setClass } from '@/lib/dom'
import { bumpGeneration } from '@/lib/images'
import { setKeyHandler } from '@/lib/input'
import type { RemoteKey } from '@/lib/keys'
import { IS_DEVICE } from '@/lib/platform'
import { isLiveEnabled } from '@/lib/settings'

/**
 * Ekran menecerı və sol rail.
 *
 * ⚠⚠ İKİ APP-IN ORTAQ MARŞRUTLAŞDIRICISIDIR (MYTasks 693, bax `lib/platform.ts`).
 *    Yığın, `back()`, `backToDetail()` və üst qat (modal) MƏNTİQİ hər iki hədəfdə
 *    eynidir — yalnız SOL RAIL cihaza məxsusdur (brauzer variantının naviqasiyası
 *    üstdədir və `browser/shell.ts`-də qurulur). Rail-a aid hər funksiya `rail`
 *    yoxdursa səssizcə heç nə etmir; ekranlar (`screens/*.ts`) hansı hədəfdə
 *    olduqlarını BİLMİR və dəyişdirilmir.
 *
 * ⚠ «Router» burada tarixçə ilə işləmir. `history.pushState` köhnə TV-də
 * ya yoxdur, ya da «Back» düyməsini app-dan çıxarır. Ona görə naviqasiya
 * sadə YIĞINDIR (stack) və Back onu bir addım geri çəkir.
 *
 * ⚠ KÖKDƏ BACK APP-DAN ÇIXMIR (2026-08-02 düzəlişi — sahib tələbi). Əvvəl
 *   `exitApp()` çağırılırdı («TV-də gözlənilən davranış» idi), amma istifadəçi
 *   veb versiyada bunun səhv təəssürat yaratdığını bildirdi (düymə basılan kimi
 *   app qapanır). İndi kökdə Back udulur — Netflix və s. kimi ana ekranda
 *   heç nə etmir, istifadəçi TV-nin öz pultu/OS-i ilə çıxır.
 */

export interface Screen {
  node: HTMLElement
  /** Ekranın öz pult məntiqi. */
  onKey(key: RemoteKey): void
  /** Rail-dan sağa keçəndə ekran fokusu geri alır. */
  takeFocus(): void
  /** Fokus rail-a keçəndə halqa gizlədilir. */
  releaseFocus(): void
  /**
   * Ekranı BAŞA qaytarır — rail-da EYNİ bənd təkrar seçiləndə çağırılır.
   *
   * ⚠ NİYƏ ƏLAVƏ OLUNDU (sahib tələbi 2026-08-25: «sol tərəfdəki homepage klikdə
   *   əgər homepage səhifəsində scroll down edibsə yenidən home klikdə əsas ekrana
   *   başa qayıtsın»). ƏVVƏL: rail cari marşrutu təkrar seçəndə `navigate` HEÇ
   *   ÇAĞIRILMIRDI (`entry.code !== currentRouteName()`) — yəni düymə ölü idi.
   *   Yenidən `navigate` etmək OLMAZDI: ekran sıfırdan qurulub bütün sıraları
   *   təkrar yükləyərdi. Bu qarmaq isə yalnız sürüşməni sıfırlayır.
   * ⚠ OPSİONALDIR: sürüşməyən ekranlar (QR, profil) onu vermir.
   */
  toTop?(): void
  /**
   * «GERİ» düyməsini ekran ÖZÜ tutur — `true` qaytarsa naviqasiya geri getmir.
   *
   * ⚠⚠ NİYƏ AYRICA QARMAQ, `onKey` KİFAYƏT ETMİR (2026-09-07-də ölçüldü):
   *   brauzer hədəfində qabıq (`browser/shell.ts`) «geri»ni ekrana ÜMUMİYYƏTLƏ
   *   ötürmür — `key === 'back'` şərtində birbaşa `back()` çağırır (ox düymələri
   *   brauzerin öz naviqasiyasıdır, ona görə körpü yalnız kino rejimində işləyir).
   *   Nəticədə profil ekranındakı PIN pad-ı «geri» ilə bağlanmırdı: ölçüldü —
   *   `Escape` basıldı, `padOpen: true` qaldı. `screens/account.ts`-də yazılmış
   *   `screen.onKey` örtüyü brauzerdə heç vaxt çağırılmırdı, yəni ölü kod idi.
   * ⚠ OPSİONALDIR: qarmağı verməyən ekranlarda davranış dəyişmir.
   */
  onBack?(): boolean
  /**
   * Ekran yığında ARXAYA DÜŞÜR (üstünə yeni ekran açılır) — sökülmür, saxlanılır.
   * `unpark` «Geri» ilə eyni nüsxə geri qoşulanda çağırılır. İzah: `enableScreenKeepAlive`.
   * ⚠ OPSİONALDIR: verməyən ekran (oynadıcı, QR, canlı) əvvəlki kimi sökülür.
   */
  park?(): void
  unpark?(): void
  destroy?(): void
}

/**
 * ÜST QAT (film modalı) — açıq olanda pult YALNIZ ona gedir.
 *
 * ⚠ NİYƏ EKRAN DEYİL: modal ekran yığınına DÜŞMÜR (`Docs/KINOBOX_DETAIL_MODAL.md`
 *   M8) — arxadakı siyahı nə açılanda, nə bağlananda yenidən qurulmur, ona görə
 *   sürüşmə mövqeyi öz-özünə qorunur. `Screen` müqaviləsinə salsaydıq `mount()`
 *   köhnə ekranı söküb yenisini qurardı və modalın bütün mənası itərdi.
 * ⚠ NİYƏ QEYDİYYAT (import DEYİL): `app.ts` modalı import etsəydi dairəvi asılılıq
 *   yaranardı (modal `navigate`-i işlədir). Bu qat isə modalın nə olduğunu bilmir —
 *   yalnız iki funksiya saxlayır.
 */
export interface Overlay {
  onKey(key: RemoteKey): void
}

/** Ekran marşrutu — hansı ekranın hansı parametrlərlə açılacağı. */
export type Route =
  | { name: 'home' }
  | { name: 'live' }
  | { name: 'search' }
  | { name: 'browse' }
  | { name: 'collections' }
  // «Tezliklə» siyahısı (MYTasks 528) — rail bəndi DEYİL, ana ekranın hero-altı
  // qısayolundan PUSH olunur (Android TV-dəki `TvRoute.CatalogList(Upcoming)` yolu).
  | { name: 'upcoming' }
  // Kolleksiya/populyar siyahı ekranı (522) — Android `TvRoute.CatalogList`-in veb
  // qarşılığı: sıra başlığındakı «Hamısı ›» (`kind: 'collection'`) və hero-altı
  // «Ən çox baxılanlar» (`kind: 'popular'`) buradan PUSH olunur.
  // ⚠ `watchlist` 2026-08-30-da əlavə olundu (sahib tələbi «sonra izlə modulu»):
  //   Profildəki bənd EYNİ siyahı ekranını açır — ayrıca ekran faylı yazmaq
  //   şəbəkə/fokus/boş vəziyyət maşınının nüsxəsi olardı (`screens/list.ts` qeydi).
  | { name: 'list'; kind: 'collection' | 'popular' | 'genre' | 'watchlist'; slug?: string; title?: string }
  | { name: 'sessions' }
  | { name: 'profile' }
  | { name: 'profiles'; replaceOnPick: boolean }
  | { name: 'qr' }
  | { name: 'detail'; slug: string }
  // Şəxs səhifəsi (2026-08-13) — Android `TvRoute.Person` qarşılığı. Axtarışdakı
  // şəxs sırasından PUSH olunur; brauzer qabığında bu marşruta keçid YOXDUR
  // (orada axtarış ayrı ekrandır — bax `browser/screens/catalog.ts`).
  | { name: 'person'; slug: string }
  // ⚠ `episodeId` + `episodes` SERİAL ÜÇÜNDÜR (2026-08-03): bilet həmin bölüm
  //   üçün istənilir və bölüm bitəndə player siyahıdakı NÖVBƏTİ bölümə keçir
  //   (Netflix məntiqi). Filmdə ikisi də verilmir.
  | {
      name: 'player'
      slug: string
      title: string
      contentId?: string
      trailerAsset?: string | null
      /**
       * Treylerin BAŞLANĞIC MÖVQEYİ (saniyə) — modaldan tam ekrana keçəndə verilir.
       *
       * ⚠ NİYƏ (sahib tələbi 2026-08-21: «fullscreen vuranda modaldakı player
       *   qaldığı yerdən davam etsin»): TV-də «tam ekran» ayrıca OYNADICI
       *   EKRANIDIR, yəni saytdakı kimi eyni `<video>` elementi qalmır — yeni
       *   ekran, yeni element. Mövqe ötürülməsə treyler həmişə sıfırdan başlayır.
       */
      trailerStartAt?: number
      /**
       * TAM EKRAN ANA EKRANIN HERO-SUNDAN AÇILIB.
       *
       * ⚠ NİYƏ LAZIMDIR (sahib tələbi 2026-08-25: «heroda trailer full screen edib
       *   fullscreendən çıxanda filmin modalına yox elə homepage qayıtmalıdır;
       *   filmin modalına o halda qayıtmalıdır ki film modaldan fullscreen
       *   edilsin»). Oynadıcıya İKİ yoldan gəlinir və çıxış onlarda FƏRQLİDİR:
       *     · modaldan → `backToDetail` (modal geri açılır, treyler qaldığı
       *       saniyədən davam edir — 2026-08-21 tələbi);
       *     · hero-dan → sadəcə `back()`, yəni ana ekran.
       *   Bayraq olmasaydı hero-dan çıxan istifadəçi heç açmadığı modalı görərdi.
       * ⚠ 2026-10-25 (MYTasks 1118): veb TV hero-sunun «İzlə»si (tam FİLM) də bu bayrağı verir
       *   və bölüm keçidi onu saxlayır — izah `browser/detailModal.ts` → `launchPlayer`.
       */
      fromHero?: boolean
      live?: boolean
      liveProgramId?: string
      episodeId?: string
      episodes?: Episode[]
      /**
       * ── SONLUQ QATI (2026-08-21) ─────────────────────────────────────────
       * «Oxşar» siyahısı və qatın açılma anı (`cnt.contents.outro_seconds`).
       *
       * ⚠ MARŞRUTLA ÖTÜRÜLÜR, PLAYER-DƏ ÇƏKİLMİR: modal onsuz da
       *   `getContent`-i çağırıb — player-dən ikinci gediş TV-də mənasız
       *   gecikmədir (kartın meta sahələri ilə eyni qərar).
       */
      similar?: ContentCard[]
      outroSeconds?: number | null
      /**
       * Kontentin yaş həddi — oynadıcı fallback/tam ekran treylerini `trailerAllowed` ilə
       * yoxlayır (2026-10-25). Verilməyibsə 0 sayılır.
       */
      ageLimit?: number | null
    }

type Factory = (route: Route) => Screen

/**
 * ⚠ `label` SAHƏSİ YOXDUR (sahib tələbi, MYTasks 567): rail YALNIZ İKONDUR.
 *   Bax `RAIL` massivinin üstündəki qeyd — bənd adları ümumiyyətlə çəkilmir,
 *   ona görə tərcümə açarı da saxlanılmır.
 */
interface RailEntry {
  code: 'home' | 'live' | 'search' | 'browse' | 'collections' | 'sessions' | 'profile'
  /** SVG işarəsi (bax `svgIcon`). */
  icon: string
}

/**
 * Rail ikonları — SVG, Unicode simvolu DEYİL.
 *
 * ⚠ NİYƏ DƏYİŞDİ (2026-08-03, sahib tələbi): əvvəl bəndlərdə hazır simvollar
 *   vardı — `▤` (əsas), `◉` (canlı), `⌕` (axtarış), `◍` (profil). Üç problemi
 *   vardı və üçü də şriftdən gəlirdi, koddan yox:
 *     • `▤` heç bir «ana səhifə» məzmunu daşımır — sadəcə zolaqlı kvadratdır;
 *     • `◉` «canlı yayım» yox, sadə dairədir (sahib: «circle çox bərbaddır»);
 *     • `⌕` şriftin em qutusunda çox KİÇİK çəkilir — yanındakı bəndlərlə eyni
 *       `font-size`-də olsa da gözlə iki dəfə balaca görünür.
 *   Simvolun ölçüsü/forması şriftin işidir, ona görə CSS ilə düzəlmirdi. SVG-də
 *   hər ikisi bizim nəzarətimizdədir və dördü də EYNİ 30×30 qutuda oturur.
 *
 * ⚠ `currentColor` — rəng `.rail__item` / `.is-active` qaydalarından gəlir, yəni
 *   aktiv bənd yenə CTA rənginə boyanır (SVG üçün ayrıca qayda lazım deyil).
 *   Köhnə TV mühərriklərində (Chrome 38+) SVG 1.1 və `currentColor` işləyir —
 *   brend monoqramı onsuz da beləcə çəkilir.
 */
function svgIcon(body: string, strokeWidth: string): string {
  return (
    '<svg class="rail__icon" width="30" height="30" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="' + strokeWidth + '" ' +
    'stroke-linecap="round" stroke-linejoin="round">' + body + '</svg>'
  )
}

/** Ev — «Əsas». */
const ICON_HOME = svgIcon(
  '<path d="M3 10.6 12 3.2l9 7.4"/><path d="M5.6 9.4V20.4h12.8V9.4"/><path d="M9.8 20.4v-5.6h4.4v5.6"/>',
  '1.8',
)

/** Yayım dalğaları + mərkəz nöqtəsi — «Canlı» (sadə dairə deyil). */
const ICON_LIVE = svgIcon(
  '<circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none"/>' +
    '<path d="M8.3 8.3a5.2 5.2 0 0 0 0 7.4"/><path d="M15.7 8.3a5.2 5.2 0 0 1 0 7.4"/>' +
    '<path d="M5.5 5.5a9 9 0 0 0 0 13"/><path d="M18.5 5.5a9 9 0 0 1 0 13"/>',
  '1.8',
)

/** Böyüdücü — «Axtarış» (qutunu tam dolduran ölçüdə). */
const ICON_SEARCH = svgIcon('<circle cx="10.4" cy="10.4" r="6.6"/><path d="M15.3 15.3 21 21"/>', '2')

/**
 * Dörd xana — «Kəşf» (kataloq).
 *
 * ⚠ Android TV-dəki `GridView` və Apple TV-dəki `square.grid.2x2.fill` ilə EYNİ
 *   fiqurdur (sahib tələbi 2026-08-03: «vebtv-yə android tv-dəki kataloq ikonunu
 *   yerləşdir»). Üç TV səthində eyni bölmənin eyni işarə ilə görünməsi vacibdir —
 *   istifadəçi bir cihazda öyrəndiyini o birində axtarır.
 */
const ICON_BROWSE = svgIcon(
  '<rect x="3.4" y="3.4" width="7.2" height="7.2" rx="1.4"/>' +
    '<rect x="13.4" y="3.4" width="7.2" height="7.2" rx="1.4"/>' +
    '<rect x="3.4" y="13.4" width="7.2" height="7.2" rx="1.4"/>' +
    '<rect x="13.4" y="13.4" width="7.2" height="7.2" rx="1.4"/>',
  '1.8',
)

/** Şəxs — «Profil». */
const ICON_PROFILE = svgIcon(
  '<circle cx="12" cy="8" r="3.6"/><path d="M4.9 20.2c0-3.6 3.2-5.9 7.1-5.9s7.1 2.3 7.1 5.9"/>',
  '1.8',
)

// ⚠ «Seanslar» 2026-07-29-da rail-dan ÇIXARILDI — bölmə bütün istehlakçı
//   səthlərində (sayt · mobil · TV) müvəqqəti bağlandı; backend və admin panel
//   toxunulmayıb. `sessions` route-u və ekranı SİLİNMİR: bənd geri qoyulanda
//   olduğu kimi işləyir.
//
// ⚠ «Kəşf» 2026-08-03-də ƏLAVƏ OLUNDU (sahib tələbi) və SIRA Apple TV / Android
//   TV ilə eynidir: Əsas · Canlı · Axtarış · Kəşf · Profil. Üç TV səthinin
//   menyusu eyni ardıcıllıqda olmalıdır — istifadəçi cihaz dəyişəndə bənd axtarmır.
//
// ⚠ «Canlı» BƏNDİ GÖRÜNMƏYƏ BİLƏR (2026-08-30): massivdən ÇIXARILMIR, sadəcə
//   `railHidden()` onu uşaq profilində və admin bayrağı bağlı olanda gizlədir.
//   Səbəb massivin indeksləridir — `railNodes[i]` ilə `RAIL[i]` cütləşir, yəni
//   sətri silmək bütün cütləri sürüşdürərdi.
//
// ⚠⚠ RAIL SABİT DAR İKON ZOLAĞIDIR — GENİŞLƏNMİR (sahib tələbi 2026-08-08,
//   MYTasks 567: «sol menyu genişlənməsin»). Əvvəl fokus rail-a düşəndə
//   `is-open` sinfi verilirdi: en 96 → 260 px, bəndlər sola düzülür və yanlarında
//   AD yazısı açılırdı. Genişlənən qat `z-index: 20` ilə məzmunun ÜSTÜNƏ minirdi,
//   yəni pult sola gedən kimi ekranın sol dörddəbiri örtülürdü.
//   Etalon üç TV səthində eynidir və ADSIZDIR: `Android/tv/.../RootView.kt` →
//   `.width(KinoboxTokens.railWidth)`, `iOS/tvOS/App/RootView.swift` →
//   `.frame(width: KinoboxTokens.railWidth)` — hər ikisi SABİT 96px, yalnız ikon.
//   Ona görə `RailEntry.label`, `.rail__label` düyünü, `repaintRailLabels()` və
//   `#rail.is-open` CSS blokları TAMAMİLƏ silindi.
const RAIL: RailEntry[] = [
  { code: 'home', icon: ICON_HOME },
  { code: 'live', icon: ICON_LIVE },
  { code: 'search', icon: ICON_SEARCH },
  { code: 'browse', icon: ICON_BROWSE },
  { code: 'collections', icon: svgIcon('<rect x="3" y="7" width="18" height="14" rx="2"/><path d="M7 3h10M7 12h10M7 16h10"/>', '1.8') },
  { code: 'profile', icon: ICON_PROFILE },
]

let factory: Factory = () => {
  throw new Error('ekran fabriki qurulmayıb')
}

const stack: Route[] = []
let screen: Screen | null = null
let overlay: Overlay | null = null
/** Film modalını açan funksiya — `main.ts` boot-da qeyd edir (bax `backToDetail`). */
let detailOpener: ((slug: string, trailerAt?: number, seed?: ContentCard) => void) | null = null
let stage: HTMLElement
/** Sol rail — YALNIZ cihaz hədəfində qurulur (brauzerdə naviqasiya üstdədir). */
let rail: HTMLElement | null = null
/** Marşrut dəyişikliyinə abunə — brauzer qabığı üst menyunu bununla işıqlandırır. */
let routeListener: ((route: Route) => void) | null = null
let railNodes: HTMLElement[] = []
let railFocused = false
let railIndex = 0
let railActive = 0

/**
 * App qabığını qurur: sol rail, ekran konteyneri, pult dinləyicisi.
 * ⚠ Bir dəfə çağırılır — DOM yenidən qurulmur (sıfır reaktivlik qaydası).
 */
export function initShell(screenFactory: Factory): void {
  factory = screenFactory
  stage = byId('stage')
  // ⚠ Brauzer variantında sol rail YOXDUR — naviqasiya üst zolaqdadır.
  if (IS_DEVICE) buildRail()
  setKeyHandler(handleKey)
}

/**
 * Marşrut dəyişəndə xəbər verir (brauzer qabığı üçün).
 *
 * ⚠ NİYƏ ABUNƏ, İMPORT DEYİL: `app.ts` brauzer qabığını import etsəydi CİHAZ
 *   bundle-ına da brauzer kodu düşərdi (ölü kod atılması `import` zəncirini
 *   kəsmir, yalnız işlədilməyən İFADƏNİ atır).
 */
export function onRouteChange(listener: (route: Route) => void): void {
  routeListener = listener
}

/**
 * Pult düyməsini qabığa ötürür.
 *
 * ⚠ NİYƏ İXRAC OLUNUR: cihaz hədəfində düymələr `lib/input.ts`-in kadr nasosundan
 *   gəlir (`setKeyHandler`). Brauzer variantı o nasosu İŞLƏTMİR — orada ox
 *   düymələri doğma `:focus`/sürüşməyə lazımdır (bax `browser/shell.ts`), ona görə
 *   klaviatura yalnız KİNO REJİMİNDƏ (player) bu funksiya ilə ötürülür.
 */
export function dispatchKey(key: RemoteKey): void {
  handleKey(key)
}

/**
 * «Geri» əvvəlcə açıq qata, sonra ekrana təklif olunur — tutulsa `true`.
 * İzahı `Screen.onBack`-dədir.
 */
export function backHandledByScreen(): boolean {
  if (overlay) return false
  return screen && screen.onBack ? screen.onBack() : false
}

/**
 * Üst qatı qeyd edir (`null` = bağlandı).
 *
 * ⚠ ARXADAKI EKRANIN FOKUS HALQASI SÖNÜR: iki halqa eyni anda görünsəydi
 *   istifadəçi pultun hansı elementi idarə etdiyini bilməzdi (§4 fokus tələsi).
 *   Bağlananda halqa EYNİ KARTA qayıdır — `Screen.takeFocus()` şəbəkənin cari
 *   indeksini işlədir, yəni siyahı da yerindən tərpənmir (M8).
 */
export function setOverlay(next: Overlay | null): void {
  overlay = next
  if (!screen) return
  if (next) screen.releaseFocus()
  else screen.takeFocus()
}

function buildRail(): void {
  rail = el('div')
  rail.id = 'rail'
  const logo = el('div', 'rail__logo')
  // Statik brend monoqramı (Logos/new/kinobox-mark.svg) — bir dəfə qurulur.
  logo.innerHTML =
    '<svg width="44" height="50" viewBox="0 -8 120 136">' +
    '<path d="M44 5.1L116.5 -6.9V126.9L44 114.9V113.1L111.5 121.2V-1.2L44 6.9Z" fill="#ab0511"/>' +
    '<g transform="translate(4.5 12.5) scale(.88 .95)">' +
    '<path d="M0 0H24V38L58 0H86L48 44L86 100H58L24 50V100H0Z" fill="#ab0511"/></g></svg>'
  rail.appendChild(logo)

  railNodes = []
  for (let i = 0; i < RAIL.length; i++) {
    const entry = RAIL[i] as RailEntry
    const node = el('div', 'rail__item focusable')
    // ⚠ `innerHTML` YALNIZ BURADA və yalnız SABİT sətirlə (yuxarıdakı ikonlar) —
    //   istifadəçi datası bu yola HEÇ VAXT düşmür. DOM bir dəfə qurulur.
    const icon = el('span', 'rail__iconbox')
    icon.innerHTML = entry.icon
    node.appendChild(icon)
    rail.appendChild(node)
    railNodes.push(node)
  }

  const viewport = byId('viewport')
  viewport.insertBefore(rail, stage)
  paintRail()
}

function paintRail(): void {
  for (let i = 0; i < railNodes.length; i++) {
    const node = railNodes[i] as HTMLElement
    setClass(node, 'is-active', i === railActive)
    // ⚠⚠ UŞAQ PROFİLİNDƏ «CANLI» BƏNDİ GİZLƏNİR (sahib tələbi 2026-08-26: uşağa özəl,
    //    SADƏ interfeys). «Canlı» xətti yayımdır — nə yaş süzgəci, nə də dayandırma
    //    var, yəni ekranda hansı kadrın çıxacağını əvvəlcədən demək olmur.
    //    Telefon və digər TV səthlərində eyni qərar.
    // ⚠ RAIL BOOT-DA BİR DƏFƏ QURULUR (sıfır reaktivlik qaydası), profil isə SONRA
    //   seçilir — ona görə bənd silinmir, GİZLƏNİR; pult da onu ötürür (`railHidden`).
    node.style.display = railHidden(i) ? 'none' : ''
    setClass(node, 'is-focused', railFocused && i === railIndex)
  }
}

// ── Naviqasiya ─────────────────────────────────────────────────────────────

/**
 * Yeni ekrana keçir. `replace` verilsə yığında sətir əvəzlənir
 * (məsələn bölümdən bölümə keçid — «Geri» 20 dəfə basılmasın deyə).
 */
export function navigate(route: Route, replace?: boolean): void {
  if (replace && stack.length > 0) stack.pop()
  else parkCurrent()
  stack.push(route)
  mount(route)
}

/*
  2026-10-23: ARXAYA DÜŞƏN EKRAN SAXLANILIR (yalnız brauzer hədəfi qoşur).
    NƏ TAPILDI: `back()` əvvəlki marşrutu `mount()` ilə SIFIRDAN qururdu — veb TV-də Kataloq →
    janr → sürüşmə → film → İzlə → Geri istifadəçini 1-ci səhifəyə, sıfırlanmış filtrə və
    səhifənin başına atırdı, fokus `<body>`-də qalırdı (komanda bildirişi).
    ƏVVƏL: hər «Geri» ekranı yenidən qurur, şəbəkədən yenidən çəkir və bütün vəziyyəti itirirdi.
    NİYƏ BELƏ: DOM bir dəfə qurulur qaydası (PERFORMANCE.md) — ekranı yenidən qurmaq əvəzinə
    nüsxə sökülmür, sadəcə səhnədən çıxarılır və geri qoşulur; filtr, yüklənmiş səhifələr,
    sıraların üfüqi sürüşməsi öz-özünə qalır. Ekran özü razılaşmalıdır (`Screen.park`):
    oynadıcı, QR və canlı kimi taymerli/videolu ekranlar əvvəlki kimi sökülür.
    ⚠ YADDAŞ HƏDDİ: ən çox `MAX_PARKED` ekran saxlanılır, artığı (ən köhnəsi) sökülür və
      ona qayıdanda əvvəlki kimi yenidən qurulur.
    ⚠ KONTEKST AÇARI: dil, profil, uşaq qatı və hesab dəyişibsə saxlanmış nüsxə ATILIR və
      ekran yenidən qurulur — böyük profilin ekranı uşaq profilinə sızmasın
      (memory: kids-layer-must-not-leak-on-profile-switch).
    ⚠ CİHAZ HƏDƏFİ TOXUNULMUR: `enableScreenKeepAlive` yalnız `browser/main.ts`-də çağırılır.
*/
const MAX_PARKED = 3

interface ParkedScreen {
  screen: Screen
  key: string
}

/** `stack[i]`-nin saxlanmış ekranı (yalnız arxadakı sətirlər üçün). */
const parked: Array<ParkedScreen | null> = []
let keepAliveKey: (() => string) | null = null

/** Brauzer hədəfi boot-da çağırır — `contextKey` dəyişibsə saxlanmış ekran atılır. */
export function enableScreenKeepAlive(contextKey: () => string): void {
  keepAliveKey = contextKey
}

function parkCurrent(): void {
  const current = screen
  if (!keepAliveKey || !current || !current.park || stack.length === 0) return
  current.park()
  if (current.node.parentNode) current.node.parentNode.removeChild(current.node)
  dropParked(stack.length - 1)
  parked[stack.length - 1] = { screen: current, key: keepAliveKey() }
  screen = null

  let count = 0
  for (let i = 0; i < parked.length; i++) if (parked[i]) count += 1
  for (let i = 0; i < parked.length && count > MAX_PARKED; i++) {
    if (!parked[i]) continue
    dropParked(i)
    count -= 1
  }
}

function dropParked(index: number): void {
  const entry = parked[index]
  if (!entry) return
  parked[index] = null
  if (entry.screen.destroy) entry.screen.destroy()
}

/** `from` və yuxarıdakı bütün saxlanmış ekranları sökür (yığın kəsiləndə). */
function dropParkedFrom(from: number): void {
  for (let i = from; i < parked.length; i++) dropParked(i)
  if (parked.length > from) parked.length = Math.max(from, 0)
}

/** Kökdə Back üçün qeyd olunmuş davranış — `main.ts` cihazda çıxış dialoqunu qeyd edir. */
let rootBackHandler: (() => void) | null = null

/**
 * Kökdə (yığında tək ekran) Back basılanda çağırılacaq funksiyanı qeyd edir.
 *
 * ⚠ 2026-09-26: ƏVVƏL kökdə Back hər iki hədəfdə udulurdu və `lib/keys.ts → exitApp()`
 *   heç yerdən çağırılmırdı. Cihazda bu, mağaza QA-sının tələbini pozur (Samsung/LG:
 *   ana ekranda Return çıxış və ya çıxış dialoqu verməlidir) və girişsiz istifadəçi
 *   QR ekranından heç cür çıxa bilmirdi (rail gizli). Brauzer hədəfi (veb TV) dəyişmir —
 *   orada udulma sahib qərarıdır (2026-08-02, veb versiya şikayəti). Qeydiyyat, import
 *   deyil: dialoq `setOverlay`-i işlədir, `app.ts` onu import etsə dairə yaranardı.
 */
export function setRootBackHandler(handler: (() => void) | null): void {
  rootBackHandler = handler
}

/** Yığında bir addım geri qayıdır; kökdə qeyd olunmuş davranış (cihaz: çıxış dialoqu) işə düşür. */
export function back(): void {
  if (stack.length <= 1) {
    if (rootBackHandler) rootBackHandler()
    return
  }
  stack.pop()
  dropParkedFrom(stack.length)
  const previous = stack[stack.length - 1]
  if (!previous) return
  const index = stack.length - 1
  const kept = parked[index]
  parked[index] = null
  if (kept && keepAliveKey && kept.key === keepAliveKey()) {
    mount(previous, kept.screen)
    return
  }
  if (kept && kept.screen.destroy) kept.screen.destroy()
  mount(previous)
}

/**
 * Film modalını açan funksiyanı qeyd edir (`main.ts` boot-da çağırır).
 *
 * ⚠ NİYƏ QEYDİYYAT, İMPORT DEYİL: `app.ts` modalı import etsəydi dairəvi asılılıq
 *   yaranardı — modal `navigate`/`setOverlay`-i işlədir. Bu qat modalın nə
 *   olduğunu bilmir, yalnız «filmi göstər» funksiyasını saxlayır.
 */
export function setDetailOpener(open: (slug: string, trailerAt?: number, seed?: ContentCard) => void): void {
  detailOpener = open
}

/**
 * Player-dən filmin ÖZÜNƏ qayıdır (sahib tələbi 2026-08-04: «geri vuranda film
 * detail səhifəsinə qayıtsın»).
 *
 * ⚠ NİYƏ SADƏCƏ `back()` DEYİL: player-ə İKİ yoldan gəlinir — filmin modalından
 *   («Filmi izlə») və ANA EKRANIN hero-sundan. İkincidə quru `back()` istifadəçini
 *   ana ekrana atır, halbuki filmi bağlayan adam filmin məlumatını gözləyir.
 *
 * ⚠ ARTIQ FİLM SƏHİFƏSİ AÇILMIR (`Docs/KINOBOX_DETAIL_MODAL.md` §1): əvvəl burada
 *   player sətri `{name:'detail'}` marşrutu ilə ƏVƏZLƏNİRDİ. İndi əvvəlki ekrana
 *   qayıdılır və filmin MODALI onun üstündə açılır — yığın ümumiyyətlə şişmir.
 * ⚠ ALTDAKI EKRAN ONSUZ DA HƏMİN FİLMİN SƏHİFƏSİDİRSƏ modal açılmır: dərin
 *   keçid/ehtiyat yolu ilə gələn istifadəçi eyni məlumatı iki qatda görməməlidir.
 */
export function backToDetail(slug: string, trailerAt?: number): void {
  // Kökdə (yığında tək sətir) geri yolu yoxdur — player ora yalnız `replace` ilə
  // düşür (canlı yayım), o da bu funksiyaya gəlmir.
  if (stack.length <= 1) return
  const below = stack[stack.length - 2]
  back()
  if (below && below.name === 'detail' && below.slug === slug) return
  // ⚠ `trailerAt` — treyler tam ekranda hansı saniyədə saxlanılıbsa, modal ORADAN
  //   davam edir (sahib tələbi 2026-08-21: «fullscreendən çıxanda da modalda
  //   qaldığı yerdən davam etsin»).
  if (detailOpener) detailOpener(slug, trailerAt)
}

/**
 * Filmin modalını ANA EKRANIN üstündə açır və yığındakı hər şeyi atır.
 *
 * ⚠⚠ NİYƏ AYRICA FUNKSİYA (sahib tələbi 2026-08-30): «film bitəndə bir neçə film
 *    çıxır hansı ki izlə vuranda həmin filmin modalı açılır. bu əladır. ama
 *    açılan modalı bağlayanda film playerə dönür ki bu səhvdir, modalı
 *    bağlayanda ana səhifəyə qayıtsın».
 *
 *    `backToDetail` BURAYA YARAMIR: o, yığından BİR sətir götürür, yəni modalın
 *    altında oynadıcının gəldiyi ekran qalır. Film artıq bitibsə həmin ekrana
 *    qayıtmaq istifadəçini baxıb qurtardığı filmin oynadıcısına geri atır.
 *    Burada yığın tamamilə boşaldılır: modalın ALTINDA yalnız ana ekran olur,
 *    ona görə modalı bağlamaq həmişə ana ekrana çıxarır.
 *
 * ⚠ YALNIZ SONLUQ EKRANINDAN ÇAĞIRILIR. Adi «geri» yolu `backToDetail`-dır və
 *   dəyişmir — orada film hələ bitməyib, istifadəçi gəldiyi yerə qayıtmalıdır.
 */
export function detailOverHome(slug: string, seed?: ContentCard): void {
  stack.length = 0
  dropParkedFrom(0)
  navigate({ name: 'home' })
  // 2026-10-25: `seed` verilirsə modal YAŞ QAPISI ilə açılır (sonluq təklifi bağlı olanda —
  //   `screens/player.ts` → `playSimilar`); verilmirsə əvvəlki kimi qapısız bərpa.
  if (detailOpener) detailOpener(slug, undefined, seed)
}

/**
 * Gizlədilmiş rail-ı YIĞILMIŞ halda geri gətirir (tam ekrandan çıxarkən).
 *
 * ⚠ NİYƏ LAZIMDIR (sahib tələbi 2026-08-03): «player timeline-da full screen
 *   iconu olmalıdır ki, vuranda fullscreen exit etsin və solda menu görünsün».
 *   Adi (canlı olmayan) player-də rail QƏSDƏN gizlədilir (`mount` — tam ekranın
 *   həqiqətən tam olması üçün). Tam ekrandan çıxanda isə menyu geri gəlməlidir,
 *   əks halda istifadəçi yalnız pultun «Geri»si ilə çıxa bilər.
 *
 * ⚠ FOKUS VERİLMİR — bu qəsdəndir: fokus rail-a keçsəydi pultun Yuxarı/Aşağısı
 *   menyunu gəzərdi, halbuki istifadəçi hələ də filmə baxır — oynatma idarəsi
 *   əlindən çıxardı. Yalnız gizlətmə götürülür, rail 96px-lik ikon zolağıdır.
 *   (Tarixçə: 2026-08-03-də bunun İKİNCİ səbəbi də vardı — `focusRail()` rail-ı
 *   260px-ə AÇIRDI. Genişlənmə 567-də tamamilə silindi, yəni bu səbəb qalxdı;
 *   davranış isə dəyişmir, çünki yuxarıdakı oynatma səbəbi qüvvədədir.)
 *
 * ⚠ QR EKRANINA TOXUNMUR: orada rail giriş qapısıdır (MYTasks 214) və heç bir
 *   yoldan açılmamalıdır.
 */
export function revealRail(): void {
  if (!rail || currentRouteName() === 'qr') return
  rail.hidden = false
}

/**
 * Rail-ı yenidən gizlədir (tam ekrana QAYIDARKƏN).
 *
 * ⚠ SAHİB TƏLƏBİ (2026-08-03): «double click edib yenidən full screen etmək
 *   istəyirəm ama menu solda qalır itmir». `#rail`-in z-index-i (20) `.player`-dən
 *   (10) yüksəkdir — brauzer öz chrome-unu gizlətsə də rail sol kənarda 96px-lik
 *   zolaq kimi qalırdı, yəni «tam ekran» tam olmurdu.
 *
 * ⚠ FOKUS DA GERİ QAYTARILIR: rail fokusda ikən sadəcə gizlətsəydik pult düymələri
 *   GÖRÜNMƏYƏN menyuya gedərdi və istifadəçi «pult işləmir» sanardı.
 */
export function hideRail(): void {
  if (!rail) return
  if (railFocused) leaveRail()
  rail.hidden = true
}

/** Ekranın öz-özünə rail-a fokus verməsi (sol kənardan çıxanda). */
export function focusRail(): boolean {
  // ⚠ GİRİŞ QAPISI (MYTasks 214): QR ekranında rail BAĞLIDIR. Əks halda istifadəçi
  //   sola basıb menyudan «Əsas»a keçir və girişsiz kataloqu gəzirdi — boot
  //   marşrutundakı yoxlama (`main.ts` → `firstRoute`) yalnız İLK ekranı seçir,
  //   ondan sonrakı naviqasiyanı saxlamırdı.
  // ⚠ Brauzer variantında rail YOXDUR — ekranların «sol kənardan çıxış» kodu
  //   dəyişmir, sadəcə `false` alıb fokusu özündə saxlayır.
  if (!rail) return false
  if (currentRouteName() === 'qr') return false
  if (railFocused) return false
  if (screen) screen.releaseFocus()
  railFocused = true
  railIndex = railActive
  paintRail()
  return true
}

/** @param restored saxlanmış nüsxə (`back()`) — verilibsə fabrik çağırılmır. */
function mount(route: Route, restored?: Screen): void {
  // Köhnə ekranın şəkil növbəsi yenisinin qabağını kəsməsin.
  bumpGeneration()

  if (screen && screen.destroy) screen.destroy()
  clear(stage)

  screen = restored || factory(route)
  stage.appendChild(screen.node)
  stage.hidden = false

  const index = railIndexOf(route)
  if (index >= 0) railActive = index

  railFocused = false
  // ⚠ GİRİŞ QAPISI (MYTasks 214): QR ekranında menyu ÜMUMİYYƏTLƏ GÖRÜNMÜR.
  //   Boot marşrutu (`main.ts` → `firstRoute`) yalnız İLK ekranı seçirdi; menyu
  //   yerində qaldığı üçün istifadəçi sola basıb (və ya siçanla klikləyib)
  //   girişsiz «Əsas»a keçə bilirdi. Elementi gizlətmək bütün yolları — pult,
  //   siçan, `focusRail()` — bir yerdən bağlayır.
  // ⚠ ADİ (CANLI OLMAYAN) PLAYER-DƏ DƏ GİZLƏNİR (2026-08-02 düzəlişi): `#rail`-in
  //   z-index-i (20) `.player`-dən (10) yüksəkdir — tam ekran (`requestFullscreen`)
  //   brauzer chrome-unu gizlədir, AMMA rail öz DOM z-qatında görünməyə davam
  //   edirdi (sol kənarda 96px-lik zolaq). Bunsuz «tam full screen» heç vaxt tam
  //   olmur. ⚠ CANLI (`route.live`) İSTİSNADIR: orada Sol düyməsi qəsdən rail-ı
  //   açır (`player.ts` → `focusRail()`) — bu axını qorumaq üçün rail gizlənmir.
  if (rail) rail.hidden = route.name === 'qr' || (route.name === 'player' && !route.live)
  paintRail()
  screen.takeFocus()

  // ⚠ EKRAN QURULANDAN SONRA: brauzer qabığı burada «kino rejimi»nə keçir və üst
  //   menyunu işıqlandırır — yəni DOM artıq yerində olmalıdır.
  if (routeListener) routeListener(route)
  // ⚠ QABIQDAN SONRA: `applyRoute` səhifəni başa atır, saxlanmış mövqe isə son söz olmalıdır.
  if (restored && restored.unpark) restored.unpark()
}

/**
 * Rail bəndi gizlidirmi. Hazırda YALNIZ «Canlı» bəndinə aiddir.
 *
 * ⚠ ADİ PROFİLDƏ VƏ BAYRAQ AÇIQ OLANDA HƏMİŞƏ `false` — yəni rail-ın davranışı
 *   bir nöqtə də dəyişmir.
 * ⚠ İKİ SƏBƏB «VƏ YA» İLƏ BİRLƏŞİR, biri o birini ƏVƏZ ETMİR:
 *     1. uşaq profili (2026-08-26) — mənbə `data-kids` atributudur (izahı
 *        `lib/profiles.ts` → `isKidsProfile`); uşaq bəndi bayraqdan ASILI
 *        OLMAYARAQ görmür (xətti yayımda nə yaş süzgəci var, nə dayandırma);
 *     2. admin bayrağı (2026-08-30, sahib tələbi) — `features.live_enabled`
 *        «true» deyilsə bənd bütün profillərdə gizlidir (`lib/settings.ts`).
 * ⚠ TƏK FUNKSİYA: `paintRail()` və pultun yuxarı/aşağı atlama məntiqi onsuz da
 *   buradan soruşur — şərt ikinci yerə yazılsaydı biri düzəlib o biri qalardı.
 * ⚠ MARŞRUT VƏ EKRAN SİLİNMİR: yalnız GİRİŞ NÖQTƏSİ itir (dərin keçid işləyir).
 */
function railHidden(index: number): boolean {
  const entry = RAIL[index]
  if (!entry || entry.code !== 'live') return false
  return document.documentElement.hasAttribute('data-kids') || !isLiveEnabled()
}

function railIndexOf(route: Route): number {
  for (let i = 0; i < RAIL.length; i++) {
    if ((RAIL[i] as RailEntry).code === route.name) return i
  }
  return -1
}

// ── Pult ───────────────────────────────────────────────────────────────────

function handleKey(key: RemoteKey): void {
  /**
   * ⚠ FOKUS TƏLƏSİ (`Docs/KINOBOX_DETAIL_MODAL.md` §4): modal açıq olanda BÜTÜN
   *   düymələr ona gedir — nə arxadakı sıra, nə menyu fokus alır. «Geri» də
   *   modalındır: o, app-dan çıxmır, pəncərəni bağlayır.
   */
  if (overlay) {
    overlay.onKey(key)
    return
  }

  if (!railFocused) {
    if (screen) screen.onKey(key)
    return
  }

  if (key === 'up') {
    // Gizli bənd ÖTÜRÜLÜR — izahı `railHidden`-dədir.
    let next = railIndex - 1
    while (next >= 0 && railHidden(next)) next -= 1
    if (next >= 0) {
      railIndex = next
      paintRail()
    }
    return
  }
  if (key === 'down') {
    let next = railIndex + 1
    while (next < railNodes.length && railHidden(next)) next += 1
    if (next < railNodes.length) {
      railIndex = next
      paintRail()
    }
    return
  }
  if (key === 'right') {
    leaveRail()
    return
  }
  if (key === 'enter') {
    const entry = RAIL[railIndex] as RailEntry
    leaveRail()
    if (entry.code !== currentRouteName()) navigate({ name: entry.code } as Route, true)
    // Eyni bənd təkrar seçildi — ekran sıfırdan qurulmur, sadəcə başa qayıdır.
    else if (screen && screen.toTop) screen.toTop()
    return
  }
  if (key === 'back') {
    // Rail-dan Back → məzmuna qayıdır (dead-end yoxdur).
    leaveRail()
  }
}

function leaveRail(): void {
  railFocused = false
  paintRail()
  if (screen) screen.takeFocus()
}

/** Yığının ən üstündəki marşrutun adı (brauzer qabığı da işlədir). */
export function currentRouteName(): string {
  const route = stack[stack.length - 1]
  return route ? route.name : ''
}

/** Naviqasiya yığınının dərinliyi — ekranların «Geri» davranışını qərarlaşdırması üçün. */
export function stackDepth(): number {
  return stack.length
}
